import axios, { AxiosHeaders, AxiosInstance, AxiosRequestConfig } from 'axios';
import { HttpClient, AxiosHttpClient, HttpMethod } from './HttpClient';
import { getEnvConfig } from '@/app/utils/env';
import { ApiErrorHandler } from './ApiErrorHandler';
import { getDecodedToken, getSessionIdentifier } from '@/app/utils/auth';
import { useAclStore } from '@/app/stores/aclStore';

type EnvConfig = ReturnType<typeof getEnvConfig>;

// Standard API response interface
export interface ApiResponse<T> {
  success: boolean;
  successful?: boolean;
  code: number;
  message: string;
  result: T;
  errors?: string[];
  timestamp: string;
}

// API service configuration
export interface ApiServiceConfig {
  baseURL: string;
  timeout?: number;
  credentials?: string;
  headers?: Record<string, string>;
  requiresAuth?: boolean;
  //mockHandler?: MockHandler;
}

// Base API service interface
export interface IApiService {
  get<T>(endpoint: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>>;
  post<T>(endpoint: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>>;
  put<T>(endpoint: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>>;
  delete<T>(endpoint: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>>;
  patch<T>(endpoint: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>>;
}

// Optimized Base API service implementation
export abstract class BaseApiService implements IApiService {
  protected httpClient: HttpClient;
  protected config: ApiServiceConfig;
  protected shouldUseMockData: boolean;

  constructor(config: ApiServiceConfig) {
    this.config = config;
    const envConfig = getEnvConfig();
    
    // Determine if we should use mock data (local or dev without credentials)
    this.shouldUseMockData = ( (envConfig.IS_LOCAL || envConfig.IS_DEVELOPMENT) && config.requiresAuth === true ) || 
      (!envConfig.API_KEY || !envConfig.API_SECRET);

    const client = this.createAxiosInstance(config, envConfig);
    this.httpClient = new AxiosHttpClient(client);
  }

  private createAxiosInstance(config: ApiServiceConfig, envConfig: EnvConfig): AxiosInstance {
    const client = axios.create({
      baseURL: config.baseURL,
      timeout: config.timeout || 10000,
      // Authenticated service instances must include credentials so the browser
      // stores and re-sends the backend SESSION cookie used for CSRF validation.
      withCredentials: config.requiresAuth === true || config.credentials === 'include',
      headers: {
      'Content-Type': 'application/json',
      ...config.headers,
      ...(envConfig.API_KEY && { 'X-RCCL-API-KEY': envConfig.API_KEY }),
      ...(envConfig.API_SECRET && { 'X-RCCL-API-SECRET': envConfig.API_SECRET }),
      },
      // Custom response transformer to handle malformed JSON from backend
      transformResponse: [(data: string) => {
        if (typeof data === 'string' && data.trim()) {
          try {
            // First, try to parse as-is
            return JSON.parse(data);
          } catch {
            // If parsing fails, try to sanitize common backend JSON errors
            // Fix: "field":}, -> "field":null,
            // Fix: "field":} -> "field":null}
            // Fix: "field":] -> "field":null]
            const sanitized = data
              .replace(/"(\w+)":\s*,/g, '"$1":null,')  // "field":, -> "field":null,
              .replace(/"(\w+)":\s*}/g, '"$1":null}')  // "field":} -> "field":null}
              .replace(/"(\w+)":\s*]/g, '"$1":null]'); // "field":] -> "field":null]
            
            try {
              console.warn('API returned malformed JSON, applied sanitization');
              return JSON.parse(sanitized);
            } catch (sanitizeError) {
              console.error('Failed to parse JSON even after sanitization:', sanitizeError);
              // Return raw data and let later error handling deal with it
              return data;
            }
          }
        }
        return data;
      }],
    });

    this.setupInterceptors(client, config, envConfig);
    return client;
  }

  private setupInterceptors(client: AxiosInstance, config: ApiServiceConfig, envConfig: EnvConfig): void {
    // Request interceptor
    client.interceptors.request.use(
      async (requestConfig) => {
        if (config.requiresAuth && (!envConfig.API_KEY || !envConfig.API_SECRET)) {
          if (!envConfig.IS_LOCAL && !envConfig.IS_DEVELOPMENT) {
            throw new Error('API credentials are required in production mode. Please set VITE_API_KEY and VITE_API_SECRET environment variables.');
          }
        }

        // Add session identifier to authenticated requests
        if (config.requiresAuth) {
          const sessionIdentifier = getSessionIdentifier();
            if (sessionIdentifier) {
              const requestHeaders = AxiosHeaders.from(requestConfig.headers);
              requestConfig.headers = requestHeaders;
              requestHeaders.set('X-RCCL-Session-ID', sessionIdentifier);
              
              // Add ACL user headers from Zustand store (non-reactive access)
              const userId = useAclStore.getState().getUserId() ?? getDecodedToken()?.UserID;
              const userGroupId = useAclStore.getState().getUserGroupId();
              
              if (userId) {
                requestHeaders.set('X-CTRAC-REQUESTOR-ID', userId);
              }
              if (userGroupId) {
                requestHeaders.set('X-CTRAC-USER-GROUP-ID', userGroupId);
              }

              
              // Require CSRF token for non-GET requests to protect against CSRF attacks.
              // Use refreshCsrfTokenIfExpired via dynamic import to avoid a static circular
              // dependency: ApiService → csrfToken → aclService → ApiService.
              if (requestConfig.method && requestConfig.method.toUpperCase() !== 'GET') {
                const { refreshCsrfTokenIfExpired } = await import('@/app/utils/csrfToken');
                const csrfToken = await refreshCsrfTokenIfExpired();
                if (csrfToken) {
                  requestHeaders.set('X-CSRF-TOKEN', csrfToken);
                }
              }
 
           }
      }

        return requestConfig;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor
    client.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401) {
          console.error('Authentication failed. Session expired or invalid.');
          
          // Only clear auth data and redirect if this service requires auth
          // Services using X-RCCL-SESSION-ID directly should not trigger logout
          if (config.requiresAuth) {
            // Clear auth data on 401 errors and trigger logout
            const { clearAuthData, getMyrclLoginUrl } = await import('@/app/utils/auth');
            clearAuthData();

            const myrclLoginUrl = getMyrclLoginUrl();
            if (myrclLoginUrl) {
              window.location.assign(myrclLoginUrl);
            } else {
              console.error('VITE_MYRCL_LOGIN_URL is not configured');
            }
          }
        } else if (error.response?.status === 403) {
          console.error('Access forbidden. Please verify API permissions.');
        } else if (error.code === 'ECONNABORTED') {
          console.error('Request timeout. Please check your network connection.');
        }
        return Promise.reject(error);
      }
    );
  }

  // Single method to handle all HTTP requests
  private async makeRequest<T>(method: HttpMethod, endpoint: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {

    try {
      const response = await this.httpClient.request<ApiResponse<T> | T>(method, endpoint, data, config);
      if (response && typeof response === 'object' && ('success' in response || 'successful' in response)) {
        const apiResponse = response as ApiResponse<T>;
        if (apiResponse.success || apiResponse.successful) {
          return {
            ...apiResponse,
            success: true,
          };
        }
        // Backend returned success:false with HTTP 2xx — preserve the failure
        // so service-layer null-guards can throw the proper error instead of
        // falling through to the generic "success" wrapper below.
        return { ...apiResponse, success: false };
      }
      
      // Response has no success/successful field — wrap as a generic success.
      return {
        success: true,
        code: 200,
        message: 'Data retrieved successfully',
        result: response as T,
        timestamp: new Date().toISOString()
      };

    } catch (error) {
      throw ApiErrorHandler.handle(error);
    }
  }


  // Simplified HTTP methods using the single makeRequest method
  async get<T>(endpoint: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.makeRequest<T>('GET', endpoint, undefined, config);
  }

  async post<T>(endpoint: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.makeRequest<T>('POST', endpoint, data, config);
  }

  async put<T>(endpoint: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.makeRequest<T>('PUT', endpoint, data, config);
  }

  async delete<T>(endpoint: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.makeRequest<T>('DELETE', endpoint, undefined, config);
  }

  async patch<T>(endpoint: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.makeRequest<T>('PATCH', endpoint, data, config);
  }
}
